<template>
  <div
    :data-index="index"
    class="
      sf-grid sf-grid-fluid sf-grid-gutter-medium sf-layer-border-bottom-dark
      workflow-review-item
      sf-layer-border-width-2
    "
  >
    <div v-if="error" class="sf-pad-h-3 sf-pad-v-2">
      <div class="sf-banner sf-banner-page-danger sf-layer-rounded">
        <div class="sf-banner-left">
          <span class="sf-banner-icon fa fa-exclamation-circle">
            <span class="sf-banner-text">
              <span class="sf-banner-title">
                {{
                  diffLimitReached
                    ? I18n.t('reconciliation_review.diff_limit_reached')
                    : I18n.t('reconciliation_review.item_error')
                }}
              </span>
            </span>
          </span>
        </div>
      </div>
    </div>
    <div v-if="bodyElement != null || headerElement != null">
      <div
        class="
          sf-row
          sf-row-nowrap
          sf-divide-h-solid
          sf-divide-snow-darker
          sf-below-0
          sf-pad-h-3
        "
      >
        <div
          class="sf-col sf-pad-b-3 sf-pad-t-4 sf-layer-border-width-1"
          :class="showCompareTemplate ? 'sf-md-6' : 'sf-md-12'"
        >
          <div
            data-testid="wf-review-item-header-row"
            class="sf-pad-l-1 tw-flex tw-items-center"
          >
            <div
              v-dompurify-html:review="headerElement"
              @click="handleHeaderClick"
            />
            <div class="tw-flex tw-items-center tw-gap-4 tw-ml-4">
              <span
                v-if="reconciliationStatusIcon"
                v-dompurify-html="reconciliationStatusIcon"
                class="sf-font-l tw-flex tw-items-center"
              ></span>
              <i
                v-if="activeAIProposal"
                v-tooltip="I18n.t('reconciliation_proposal.confirm.awaiting')"
                class="
                  icon icon-magic
                  tw-text-violet-500 tw-scale-150 tw-text-xs
                "
              ></i>
              <workflow-review-checkmark
                v-if="workflowReviewStatusData"
                class="tw-flex tw-items-center"
                :review="workflowReviewStatusData.review"
                :reviews="workflowReviewStatusData.reviews"
              />
              <SfDrawerToggle
                v-if="showMessageCount"
                position="right"
                content="communication"
              >
                <span class="sf-level sf-link">
                  {{ messageCountElement }}
                </span>
              </SfDrawerToggle>
            </div>
          </div>
        </div>
        <div v-if="showCompareTemplate" class="sf-col sf-md-6 sf-pad-t-3" />
      </div>
      <div
        class="
          sf-row
          sf-row-nowrap
          sf-divide-h-solid
          sf-divide-snow-darker
          sf-below-0
          sf-pad-h-3
          compare-diff
        "
      >
        <div
          class="sf-col sf-pad-b-2 sf-layer-border-width-1"
          :class="showCompareTemplate ? 'sf-md-6' : 'sf-md-12'"
        >
          <div
            v-if="hasBodyElement || subAdjustmentsElement"
            class="sf-layer-rounded sf-layer-white sf-pad-2 sf-scrollbar"
          >
            <div
              class="sf-col rendered-markdown sf-markdown"
              style="overflow: unset !important"
              data-behaviour="locked-live-inputs"
              :class="hideSessionClass"
            >
              <div
                v-if="subAdjustmentsElement"
                v-dompurify-html="subAdjustmentsElement"
              />

              <div
                v-if="bodyElement"
                ref="bodyElement"
                v-dompurify-html="bodyElement"
                data-testid="review-item"
              />
              <div v-else>
                <div class="sf-inline-block sf-pad-l-2">
                  <div class="sf-spinner" />
                </div>
              </div>
            </div>
          </div>

          <div v-else class="sf-layer-rounded sf-layer-white">
            <div
              class="sf-col sf-pad-4 sf-align-c sf-font-l"
              data-testid="review-item"
            >
              {{ I18n.t('company_workflows.empty_reconciliation') }}
            </div>
          </div>
        </div>
        <div
          v-if="showCompareTemplate"
          class="sf-col sf-md-6 sf-pad-b-2 right-compare"
        >
          <div
            v-if="hasCompareBodyElement"
            class="sf-layer-rounded sf-layer-white sf-pad-2 sf-scrollbar"
          >
            <div
              class="sf-col rendered-markdown sf-markdown"
              style="overflow: unset !important"
            >
              <div v-dompurify-html="compareBodyElement" />
            </div>
          </div>

          <div v-else class="sf-layer-rounded sf-layer-white">
            <div class="sf-col sf-pad-4 sf-align-c sf-font-l">
              {{ I18n.t('company_workflows.empty_reconciliation') }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="
          sf-row
          sf-row-nowrap
          sf-divide-h-solid
          sf-divide-snow-darker
          sf-below-0
          sf-pad-h-3
        "
      >
        <div
          class="sf-col sf-pad-b-4 sf-layer-border-width-1"
          :class="showCompareTemplate ? 'sf-md-6' : 'sf-md-12'"
        >
          <workflow-review-status
            v-if="workflowReviewStatusData && workflowReviewStatusData.itemId"
            v-bind="workflowReviewStatusData"
            :parent-id="id"
            :tracking-prefix="'review-screen'"
            @item-review-update="updateReviewItemStatus"
          />
        </div>
        <div v-if="showCompareTemplate" />
      </div>
    </div>
    <div v-else class="sf-pad-h-3 sf-pad-v-2">
      <div class="sf-layer-rounded sf-layer-white sf-pad-2 sf-pad-v-2">
        {{ itemName }}
        <div class="sf-inline-block sf-pad-l-2">
          <div class="sf-spinner" />
        </div>
      </div>
    </div>
    <!-- syncScroll buttons -->
    <div v-if="showScrollToggle" class="sync-buttons">
      <div
        v-for="index in 2"
        :key="index"
        v-tooltip="syncScrollTooltip"
        :class="syncEnabled ? '' : 'unlinked'"
        class="sf-btn sf-btn-small sf-dropdown link-button"
        @click="toggleScrollSync"
      >
        <i :class="syncIcon" />
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import { throttle } from 'lodash'
import { mapActions, mapState as mapPiniaState } from 'pinia'
import { nextTick } from 'vue'

import LiveInputs from './../../../../reconciliation/handlers/live_inputs'
import WorkflowReviewStatus from './workflow-review-status.vue'
import WorkflowReviewCheckmark from './../../../sections/workflow-review-checkmark.vue'
import SfDrawerToggle from '@app/shared/vue/connected/sf-drawer-toggle.vue'
import buildLiveInputBubbleMounter from '@app/application/communication/handlers/live-input-bubble-mounter'
import { useWorkflowReviewFocus } from '../store/pinia/workflow-review-focus'
import { useWorkflowReviewItemStatus } from '../store/pinia/workflow-review-item-status'
import { useWorfklowUI } from '../store/pinia/workflow-ui'

import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'

export default {
  components: {
    WorkflowReviewStatus,
    WorkflowReviewCheckmark,
    SfDrawerToggle,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    bodyElement: {
      type: String,
    },
    hasBodyElement: {
      type: Boolean,
      default: true,
    },
    hasCompareBodyElement: {
      type: Boolean,
      default: true,
    },
    compareBodyElement: {
      type: String,
    },
    subAdjustmentsElement: {
      type: String,
    },
    focused: {
      type: Boolean,
    },
    getScrollableRef: {
      type: Function,
    },
    headerElement: {
      type: String,
    },
    reconciliationStatusIcon: {
      type: String,
    },
    index: {
      type: Number,
    },
    itemName: {
      type: String,
    },
    messageCountElement: {
      type: String,
    },
    observer: {
      type: IntersectionObserver,
    },
    shouldFocus: {
      type: Boolean,
    },
    shouldLinkInputsToRemarks: {
      type: Boolean,
    },
    workflowReviewStatusData: {
      type: Object,
    },
    error: {
      type: Boolean,
    },
    diffLimitReached: {
      type: Boolean,
    },
    activeAIProposal: {
      type: Boolean,
    },
  },
  setup() {
    const hideSessionClass = useRedactSessionRecording()
    return {
      hideSessionClass,
    }
  },
  data() {
    return {
      inputsToRemarksLinker: null,
      liveInputsRef: null,
      parentEl: this.getScrollableRef(),
      syncEnabled: true,
      showScrollToggle: false,
      syncScrollSetup: false,
    }
  },
  computed: {
    ...mapPiniaState(useWorfklowUI, ['compareMode']),
    showMessageCount() {
      return this.messageCountElement
    },
    showCompareTemplate() {
      return this.compareMode && this.compareBodyElement !== null
    },
    syncScrollTooltip() {
      return this.syncEnabled
        ? I18n.t('reconciliation_review.disable_synchronous_scrolling')
        : I18n.t('reconciliation_review.enable_synchronous_scrolling')
    },
    syncIcon() {
      return this.syncEnabled ? 'icon-link' : 'icon-unlink'
    },
  },
  watch: {
    bodyElement() {
      nextTick(() => {
        this.initDOMHandlers()

        if (this.focused) {
          this.initLiveInputs()
          this.registerInputRemarkerLinker()
        }

        // When we load this review-item the height changes in comparisan to the initial placeholder
        // We adjust the scrollbar for this, so the current review-item stays in view.
        // If async-liquid FF is enabled, this will be triggered twice:
        // 1 when the 'regular' data arrives, 1 when the liquid body arrives, instead of only once.
        if (this.$el.getBoundingClientRect().top < 0) {
          this.parentEl.scrollTop =
            this.scrollTop + $(this.$el).outerHeight(true) - this.initialHeight
        }
        this.addScrollListener()
      })
    },
    focused: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          if (newValue) {
            this.initLiveInputs()
          } else {
            this.unmountLiveInputs()
          }
        }
      },
      immediate: true,
    },
    shouldFocus(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.$el.scrollIntoView(true)
        this.completeNextReviewItemFocus()
      }
    },
  },
  mounted() {
    this.initialHeight = this.initialHeight || $(this.$el).outerHeight(true)

    if (this.focused) {
      this.parentEl.scrollTop = this.$el.offsetTop
      this.initLiveInputs()
    }

    this.initDOMHandlers()
    this.addScrollListener()
  },
  created() {
    window.addEventListener('linkInputComms', this.registerInputRemarkerLinker)
  },
  beforeUnmount() {
    window.removeEventListener(
      'linkInputComms',
      this.registerInputRemarkerLinker
    )

    this.unmountLiveInputs()
  },
  beforeUpdate() {
    this.scrollTop = this.parentEl.scrollTop
    this.initialHeight = $(this.$el).outerHeight(true)
  },
  methods: {
    ...mapActions(useWorkflowReviewFocus, ['completeNextReviewItemFocus']),
    ...mapActions(useWorkflowReviewItemStatus, ['updateReviewItemStatus']),
    initDOMHandlers() {
      this.observer.observe(this.$el)

      // Force initilization of newly added piece of DOM
      //
      // Previous code was overhelming for browser, it was initializing many behaviours on every request
      // I will leave old code here in case someone searches codebase for this phrase:
      // $(this.$el).initializeDom()
      //
      // New code is only initializing DOM for some particular behaviours (e.g. Tooltips)
      // It is possible that we will add more initialiers later

      const $el = $(this.$el)
      $el.trigger('tooltipsRendered', $el)
      $el.trigger('initAccountEntryModal', $el)
    },
    initLiveInputs() {
      // Unmount any existing stale live inputs
      this.unmountLiveInputs()

      const $el = $(this.$el)
      this.liveInputsRef = LiveInputs.initialize($el)
    },
    unmountLiveInputs() {
      this.liveInputsRef?.forEach((instance) => {
        instance.teardown()
      })

      this.liveInputsRef = null
    },
    cleanupInputRemarkLinker() {
      if (this.inputsToRemarksLinker) {
        this.inputsToRemarksLinker.teardown()
        this.inputsToRemarksLinker = null
      }
    },
    registerInputRemarkerLinker() {
      const target = this.$refs?.bodyElement

      if (this.shouldLinkInputsToRemarks && target) {
        this.cleanupInputRemarkLinker()
        this.inputsToRemarksLinker = buildLiveInputBubbleMounter(true)

        this.inputsToRemarksLinker.mount({
          container: target,
        })
      }
    },
    toggleScrollSync() {
      this.syncEnabled = !this.syncEnabled
    },

    addScrollListener() {
      if (!this.compareMode || this.syncScrollSetup) {
        return
      }

      this.syncScrollSetup = true

      const scrollBoxes = this.$el.querySelectorAll(
        '.compare-diff .sf-scrollbar'
      )
      if (scrollBoxes.length === 2) {
        this.syncScroll(scrollBoxes[0], scrollBoxes[1])
        this.syncScroll(scrollBoxes[1], scrollBoxes[0])

        this.showScrollToggle =
          scrollBoxes[0].scrollWidth > scrollBoxes[0].clientWidth &&
          scrollBoxes[1].scrollWidth > scrollBoxes[1].clientWidth

        if (this.showScrollToggle) {
          nextTick(this.moveSyncButtons)
        }
      }
    },
    moveSyncButtons() {
      // DOM manipulation is not good inside Vuejs
      // but we need to move these buttons to position correctly
      const reviewItem = this.$el.querySelector('.rendered-markdown')
      const linkButton = this.$el.querySelector('.sync-buttons .link-button')
      if (reviewItem && linkButton) reviewItem.after(linkButton)

      const reviewItemCompare = this.$el.querySelector(
        '.right-compare .rendered-markdown'
      )
      //the second button has the same selector because the first button wil already be removed
      const linkButtonCompare = this.$el.querySelector(
        '.sync-buttons .link-button'
      )
      if (reviewItemCompare && linkButtonCompare)
        reviewItemCompare.after(linkButtonCompare)
    },
    handleHeaderClick(e) {
      if (e.target.href) {
        e.preventDefault()
        e.stopPropagation()
        window.Turbolinks.visit(e.target.href)
      }
    },
    syncScroll(element1, element2) {
      const debouncedFn = throttle(() => {
        if (!this.syncEnabled) return
        element2.scrollTo(element1.scrollLeft, 0)
      }, 30)

      element1.addEventListener('scroll', debouncedFn, { passive: true })
    },
  },
}
</script>

<style>
.compare-diff ins {
  text-decoration: none;
  color: #26af87;
  background-color: #e4f9f3;
}

.compare-diff del {
  text-decoration: none;
  color: #ff6b6b;
  background-color: #fff0f0;
}

.sf-layer-border-width-1 {
  border-width: 2px;
}

.sf-layer-border-width-2 {
  border-width: 4px;
}

.link-button {
  padding: 4px 7px;
  font-size: 14px;
  position: sticky;
  left: 0;
  opacity: 0.8;
}

.link-button.unlinked {
  box-shadow: none;
  text-decoration: none;
  background-color: #f2f2f2;
  color: #343e4b;
}

.sf-margin-r-0 {
  margin-right: 0 !important;
}
</style>
